import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Loader2, RotateCcw, Trash2 } from 'lucide-react';
import { bulkAPI } from '../../services/api';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { PaginationControls } from '../../components/PaginationControls';
import { BULK_MODULES } from '../../components/bulk/BulkActionBar';

export default function RecycleBinPage() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [restoring, setRestoring] = useState(false);
  const [purging, setPurging] = useState(false);
  const [entity, setEntity] = useState('all');
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(50);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [selected, setSelected] = useState(() => new Set());

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const response = await bulkAPI.recycleBin({
        entity: entity === 'all' ? undefined : entity,
        page,
        limit,
      });
      setRows(response.data.items || []);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to load deleted records');
    } finally {
      setLoading(false);
    }
  }, [entity, page, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [entity, limit]);

  const key = (row) => `${row.entity}::${row.id}`;
  const toggle = (row) => setSelected((current) => {
    const next = new Set(current);
    const id = key(row);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const togglePage = () => setSelected((current) => {
    const next = new Set(current);
    const everyOn = rows.length > 0 && rows.every((row) => next.has(key(row)));
    rows.forEach((row) => (everyOn ? next.delete(key(row)) : next.add(key(row))));
    return next;
  });

  const restore = async () => {
    const grouped = {};
    Array.from(selected).forEach((id) => {
      const [entityKey, recordId] = id.split('::');
      grouped[entityKey] = grouped[entityKey] || [];
      grouped[entityKey].push(recordId);
    });
    setRestoring(true);
    try {
      let restored = 0;
      for (const [entityKey, ids] of Object.entries(grouped)) {
        // eslint-disable-next-line no-await-in-loop
        const response = await bulkAPI.restore(entityKey, ids);
        restored += response.data.restored_count || 0;
      }
      toast.success(`${restored} record(s) restored`);
      setSelected(new Set());
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Restore failed');
    } finally {
      setRestoring(false);
    }
  };

  const purge = async () => {
    const typed = window.prompt('Permanently erase the selected records? This cannot be undone.\n\nType PURGE to confirm:');
    if (!typed) return;
    const grouped = {};
    Array.from(selected).forEach((id) => {
      const [entityKey, recordId] = id.split('::');
      grouped[entityKey] = grouped[entityKey] || [];
      grouped[entityKey].push(recordId);
    });
    setPurging(true);
    try {
      let purged = 0;
      for (const [entityKey, ids] of Object.entries(grouped)) {
        // eslint-disable-next-line no-await-in-loop
        const response = await bulkAPI.purge(entityKey, ids, typed);
        purged += response.data.purged_count || 0;
      }
      toast.success(`${purged} record(s) permanently erased`);
      setSelected(new Set());
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Purge failed');
    } finally {
      setPurging(false);
    }
  };

  const allOnPageSelected = rows.length > 0 && rows.every((row) => selected.has(key(row)));

  return (
    <div className="space-y-6 animate-fade-in" data-testid="recycle-bin-page">
      <div>
        <h1 className="text-3xl font-heading font-bold uppercase tracking-tight">Recently Deleted</h1>
        <p className="text-muted-foreground">
          Records removed by a bulk delete. Nothing is erased — restore anything back into its module.
        </p>
      </div>

      <Card className="card-dashboard">
        <CardHeader>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <CardTitle className="flex items-center gap-2 text-lg font-heading uppercase">
              <Trash2 className="h-5 w-5 text-primary" /> {total} deleted records
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={entity} onValueChange={setEntity}>
                <SelectTrigger className="w-[190px]" data-testid="recycle-bin-module-filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All modules</SelectItem>
                  {Object.entries(BULK_MODULES).map(([value, config]) => (
                    <SelectItem key={value} value={value}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                onClick={restore}
                disabled={selected.size === 0 || restoring}
                data-testid="recycle-bin-restore-btn"
              >
                {restoring ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RotateCcw className="mr-2 h-4 w-4" />}
                Restore {selected.size > 0 ? `(${selected.size})` : ''}
              </Button>
              <Button
                variant="destructive"
                onClick={purge}
                disabled={selected.size === 0 || purging}
                data-testid="recycle-bin-purge-btn"
                title="Permanently erase the selected records (admin only)"
              >
                {purging ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-2 h-4 w-4" />}
                Erase permanently
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : rows.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground" data-testid="recycle-bin-empty">
              <Trash2 className="mx-auto mb-4 h-12 w-12 opacity-50" />
              <p>Nothing has been bulk-deleted.</p>
            </div>
          ) : (
            <div className="table-wrapper overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="table-header">
                    <TableHead className="w-10">
                      <Checkbox
                        checked={allOnPageSelected}
                        onCheckedChange={togglePage}
                        aria-label="Select all deleted records on this page"
                        data-testid="recycle-bin-select-all-page"
                      />
                    </TableHead>
                    <TableHead>Module</TableHead>
                    <TableHead>Reference</TableHead>
                    <TableHead>Deleted by</TableHead>
                    <TableHead>Deleted at</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={key(row)} className="table-row" data-testid={`recycle-bin-row-${row.id}`}>
                      <TableCell>
                        <Checkbox
                          checked={selected.has(key(row))}
                          onCheckedChange={() => toggle(row)}
                          aria-label={`Select ${row.reference}`}
                          data-testid={`recycle-bin-checkbox-${row.id}`}
                        />
                      </TableCell>
                      <TableCell><Badge variant="outline">{row.entity_label}</Badge></TableCell>
                      <TableCell className="font-mono font-medium">{row.reference}</TableCell>
                      <TableCell>{row.deleted_by_name || '—'}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.deleted_at ? new Date(row.deleted_at).toLocaleString() : '—'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <PaginationControls
            page={page}
            totalPages={totalPages}
            total={total}
            limit={limit}
            onLimitChange={setLimit}
            onPageChange={setPage}
          />
        </CardContent>
      </Card>
    </div>
  );
}
